<template>
  <div class="module-container eligibility-container">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <div class="d-flex flex-row justify-content-start align-items-center">
        <div
          class="main-image-container bg-purple rounded d-flex justify-content-center align-items-center"
        >
          <img
            height="25"
            width="25"
            :src="require('../../assets/icons/documents.svg')"
          />
        </div>
        <h3 class="mb-0 ml-3">Eligibility</h3>
      </div>
      <div class="d-flex">
        <b-button
          variant="primary"
          class="smaller-button button-custom-blue"
          @click="addEligibility"
        >
          New verification
        </b-button>
      </div>
    </div>
    <div class="filters-container" ref="filtersContainer">
      <eligibility-index-filter
        v-model="filter"
        :hasFilters="hasFilters"
        :loading="loading"
      />
    </div>

    <div class="mt-3 mb-0 w-100 d-flex flex-row justify-content-end">
      <div class="d-flex flex-row justify-content-end align-items-center">
        <top-paginator
          :total="verificationCount"
          :page="filter.page"
          :perPage="filter.pageSize"
          @change-page="changePage"
        />
      </div>
    </div>

    <div
      :style="
        `--eligibility-filters-container-height:calc(${filtersContainerHeight}px);`
      "
    >
      <grid
        :autoHeight="true"
        :columns="columns"
        :data="verifications"
        :store="gridConfig.store"
        :columnLines="gridConfig.columnLines"
        :searchFeature="true"
        @storesort="listeners.storeSort"
        @renderrows="hookDropdownButtons"
        @renderrow="hookDropdownButtons"
      />
    </div>

    <add-edit-eligibility-modal
      v-if="showAddEditVerificationModal"
      v-model="showAddEditVerificationModal"
      :eligibilityVerificationId="verificationId"
      @onModify="onModifyAddVerification"
    />
    <eligibility-request-render-modal
      v-if="showVerificationRequestRenderModal"
      v-model="showVerificationRequestRenderModal"
      :eligibilityVerificationId="verificationId"
    />
    <create-eoc-from-patient-modal
      ref="createEocModal"
      :eligibilityVerificationId="verificationId"
      @onAddEditEoc="onAddEditEoc"
    />
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid.vue";
import TopPaginator from "../controls/TopPaginator.vue";
import CreateEocFromPatientModal from "../episode_of_care/CreateEocFromPatientModal.vue";
import AddEditEligibilityModal from "./AddEditEligibilityModal.vue";
import EligibilityIndexFilter from "./EligibilityIndexFilter.vue";
import EligibilityRequestRenderModal from "./EligibilityRequestRenderModal.vue";
import { formatDateOnly } from "../../helpers/date";
import eligibilityVerificationServer from "../../server/eligibilityVerification";

export default {
  name: "EligibilityIndex",
  components: {
    Grid,
    TopPaginator,
    AddEditEligibilityModal,
    EligibilityIndexFilter,
    EligibilityRequestRenderModal,
    CreateEocFromPatientModal
  },
  props: ["showing"],
  data() {
    return {
      verifications: [],
      verificationCount: 0,
      loading: false,
      filter: {
        page: 0,
        pageSize: 50,
        orderBy: null,
        orderByDescending: true,
        searchPhrase: null,
        subscriberType: [],
        location: [],
        insurance: [],
        startDateFrom: null,
        startDateTo: null,
        endDateFrom: null,
        endDateTo: null
      },
      filtersContainerHeight: 0,
      showAddEditVerificationModal: false,
      verificationId: null,
      showVerificationRequestRenderModal: false,
      listeners: {
        storeSort: sortData => {
          if (sortData.sorters.length == 1) {
            const sorter = sortData.sorters[0];
            const ascending = sorter.ascending;
            const field = sorter.field;
            this.filter.orderBy = field;
            this.filter.orderByDescending = !ascending;
            this.changePage(0);
          }
        }
      },
      gridConfig: {
        minHeight: "20em",
        columnLines: false
      }
    };
  },
  methods: {
    addEligibility() {
      this.verificationId = null;
      this.showAddEditVerificationModal = true;
    },
    async fetchVerifications() {
      this.loading = true;
      const result = await eligibilityVerificationServer.query({
        page: this.filter.page,
        pageSize: this.filter.pageSize,
        orderBy: this.filter.orderBy,
        orderByDescending: this.filter.orderByDescending,
        searchPhrase: this.filter.searchPhrase,
        subscriberType: this.filter.subscriberType.map(u => u.value),
        locationId: this.filter.location.map(u => u.value),
        insuranceId: this.filter.insurance.map(u => u.value),
        serviceDateStartFrom: this.filter.startDateFrom,
        serviceDateStartTo: this.filter.startDateTo,
        serviceDateEndFrom: this.filter.endDateFrom,
        serviceDateEndTo: this.filter.endDateTo
      });
      this.verifications = result.records;
      this.verificationCount = result.count;
      this.loading = false;
    },
    changePage(page) {
      this.filter.page = page;
      this.fetchVerifications();
    },
    removeFilter(filter, index) {
      const newValue = [...this.filter[filter]];
      newValue.splice(index, 1);
      this.filter[filter] = newValue;
    },
    computeHeights() {
      setTimeout(() => {
        const filtersContainerHeight = this.$refs.filtersContainer.clientHeight;
        this.filtersContainerHeight = filtersContainerHeight;
      }, 0);
    },
    onModifyAddVerification(params) {
      if (params?.showResultModal) {
        this.showVerificationRequestRenderModal = true;
        this.verificationId = params?.verificationId;
      }
      this.fetchVerifications();
    },
    onAddEditEoc() {
      this.fetchVerifications();
    }
  },
  computed: {
    hasFilters() {
      return (
        this.filter.insurance.length > 0 ||
        this.filter.location.length > 0 ||
        this.filter.subscriberType.length > 0 ||
        this.filter.startDateFrom ||
        this.filter.startDateTo ||
        this.filter.endDateFrom ||
        this.filter.endDateTo
      );
    },
    columns() {
      return [
        {
          text: "Subscriber",
          field: "name",
          flex: 1,
          type: "template",
          template: ({ record }) => {
            return `${record.memberLastName}, ${record.memberFirstName} ${
              record.memberDateOfBirth
                ? "(" + formatDateOnly(record.memberDateOfBirth) + ")"
                : ""
            }`;
          }
        },
        {
          text: "Insurance Company",
          field: "insurance.name",
          flex: 1
        },
        {
          text: "Eligibility State",
          field: "state",
          width: 160,
          type: "template",
          htmlEncode: false,
          template: ({ record }) => {
            return record.activeCoverage
              ? `<span class="text-success">Active Coverage</span>`
              : `<span class="text-danger">Inactive Coverage</span>`;
          }
        },
        {
          text: "Service Code",
          field: "serviceCode",
          flex: 1,
          type: "template",
          template: ({ record }) => {
            return `${record.data.serviceCode.name} (${record.data.serviceCode.code})`;
          }
        },
        {
          text: "Service Dates",
          field: "serviceDates",
          width: 190,
          type: "template",
          template: ({ record }) => {
            return `${formatDateOnly(record.serviceDateStart)}
            -
            ${formatDateOnly(record.serviceDateEnd)}`;
          }
        },
        {
          text: "Verified By",
          field: "verifiedBy",
          flex: 1,
          type: "template",
          template: ({ record }) => {
            return this.formatUser(record.verifiedBy);
          }
        },
        {
          text: "Location",
          field: "location.name",
          flex: 1
        },

        {
          text: "Tools",
          type: "action",
          cellCls: "pointer-cell",
          align: "right",
          width: 80,
          actions: [
            {
              tooltip: "View",
              renderer: () =>
                `<i class="fa fa-search-plus color-custom-dark-grey hover-text-primary b-action-item  grid-action-icon"></i>`,
              onClick: ({ record }) => {
                this.verificationId = record.id;
                this.showVerificationRequestRenderModal = true;
              }
            },
            {
              tooltip: "More",
              renderer: ({ record }) => {
                const actions = [];

                const eligibility = this.verifications.find(
                  u => u.id == record.id
                );
                if (!eligibility.episodeOfCare) {
                  actions.push({
                    content: "Add Patient",
                    callback: () => {
                      this.verificationId = record.id;
                      this.$refs["createEocModal"].show();
                    }
                  });
                }
                actions.push({
                  content: "Re-run",
                  callback: () => {
                    this.verificationId = record.id;
                    this.showAddEditVerificationModal = true;
                  }
                });
                actions.push({
                  content: "Delete",
                  callback: () => {
                    this.$dialog
                      .confirm(
                        {
                          title: "Are you sure you want to delete this record?"
                        },
                        {
                          view: "confirm-delete"
                        }
                      )
                      .then(async () => {
                        let result = await eligibilityVerificationServer.delete(
                          record.id
                        );
                        if (result.errorCode) {
                          this.$dialog.alert("Error: " + result.message);
                        } else {
                          this.fetchVerifications();
                          this.showAlertSuccess();
                        }
                      })
                      .catch(function() {});
                  }
                });

                return this.createDropdownButton({
                  items: actions
                });
              }
            }
          ]
        }
      ];
    }
  },
  watch: {
    showing: {
      handler: function(newValue) {
        if (newValue) {
          this.fetchVerifications();
          this.computeHeights();
        }
      },
      immediate: true
    },
    filter() {
      if (this.showing) {
        this.filter.page = 0;
        this.fetchVerifications();
        this.computeHeights();
      }
    }
  }
};
</script>

<style scoped>
.eligibility-container >>> .b-grid-container .b-container {
  max-height: calc(100vh - var(--eligibility-filters-container-height) - 240px);
}
.eligibility-container >>> .b-grid-row {
  background-color: #ffffff;
}
.eligibility-container >>> .b-grid-row.b-group-row {
  background-color: #f6f6f6;
}
.eligibility-container >>> .b-action-ct {
  display: flex;
  align-items: center;
}
</style>
